import { isPast, isToday } from 'date-fns'
import { endOfDay } from 'date-fns/esm'
import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Flex, Margin, Padding } from '@/@bothrs-ui/web/components/layout'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { capitalize, formatLocale } from '@/modules/shared/helpers/locale'
import { TimelineActivityCard } from '../timeline-activity-card/TimelineActivityCard'
import { TimelineEmptyDay } from '../timeline-empty-day/TimelineEmptyDay'
import {
  StActivitiesContainer,
  StDate,
  StDateContainer,
  StDot,
  StDotLineContainer,
  StItemContainer,
} from './TimelineItem.styled'

import type { TimelineDay } from '../../Timeline'

type Props = {
  day: TimelineDay
  animationOffset: number
}

const TimelineItem = ({ day }: Props) => {
  const theme = useTheme()

  const isMobile = useMediaQuery(breakpoints.toS)
  const dayString = capitalize(formatLocale(day.date, isMobile ? 'eeeeee' : 'eeee'))
  const dayNumberString = formatLocale(day.date, 'dd')

  const itemIsToday = useMemo(() => isToday(day.date), [day.date])

  return (
    <StItemContainer isToday={itemIsToday}>
      {/* Adding an anchor with an offset of the month header to nicely align*/}
      {itemIsToday ? <div id={'timelineTodayAnchor'} style={{ position: 'absolute', top: '-80px' }} /> : null}
      <Padding py={itemIsToday ? theme.UI.Spacing.Space5 : theme.UI.Spacing.Space2}>
        <Flex alignItems='stretch'>
          <StDateContainer>
            <StDate alignItems='center' gap={theme.UI.Spacing.Space1}>
              <Span
                type='BodyMediumRegular'
                color={itemIsToday ? theme.Components.Button.Primary.Fill : theme.Colors.Neutrals.Dark}
              >
                {dayString}
              </Span>
              <Span
                type='BodyMediumRegular'
                color={itemIsToday ? theme.Components.Button.Primary.Fill : theme.Colors.Neutrals.Dark}
              >
                {dayNumberString}
              </Span>
            </StDate>
            <Margin mb={theme.UI.Spacing.Space1} />
            <StDotLineContainer alignSelf='stretch' alignItems='center' flexGrow={1} gap={theme.UI.Spacing.Space2}>
              <WebPlatform>
                <StDot color={itemIsToday ? theme.Components.Button.Primary.Fill : theme.Colors.Neutrals.Light} />
              </WebPlatform>
              <Line
                isVertical
                thickness={2}
                color={itemIsToday ? theme.Components.Button.Primary.Fill : theme.Colors.Neutrals.Light}
              />
            </StDotLineContainer>
          </StDateContainer>
          <StActivitiesContainer>
            {day.activities.length === 0 ? (
              <TimelineEmptyDay />
            ) : (
              day.activities.map((activity) => (
                <motion.div
                  key={`${activity.type}_${activity.id}_${activity.completed}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    delay: 0.1,
                  }}
                  onClick={activity.onClick}
                >
                  <TimelineActivityCard {...activity} hasPassed={isPast(endOfDay(new Date(activity.start)))} />
                </motion.div>
              ))
            )}
          </StActivitiesContainer>
        </Flex>
      </Padding>
    </StItemContainer>
  )
}

export { TimelineItem }
